import React from 'react';
import { Table, Tag, Button, Popconfirm, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ValidationAlert, AlertSeverity, AlertType } from '../../services/validation';

interface AlertTableProps {
    alerts: ValidationAlert[];
    loading: boolean;
    onResolve: (id: number) => void;
}

const severityColor = (severity: AlertSeverity) => {
    switch (severity) {
        case AlertSeverity.CRITICAL:
            return 'red';
        case AlertSeverity.ERROR:
            return 'volcano';
        case AlertSeverity.WARN:
            return 'orange';
        case AlertSeverity.INFO:
            return 'blue';
        default:
            return 'default';
    }
};

const AlertTable: React.FC<AlertTableProps> = ({ alerts, loading, onResolve }) => {
    const columns: ColumnsType<ValidationAlert> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleString(),
            width: 180,
        },
        {
            title: '严重级',
            dataIndex: 'severity',
            key: 'severity',
            render: (severity: AlertSeverity) => (
                <Tag color={severityColor(severity)}>{severity}</Tag>
            ),
            width: 100,
            filters: Object.values(AlertSeverity).map(s => ({ text: s, value: s })),
            onFilter: (value, record) => record.severity === value,
        },
        {
            title: '类型',
            dataIndex: 'alert_type',
            key: 'alert_type',
            render: (type: AlertType) => <Tag>{type}</Tag>,
            width: 120,
        },
        {
            title: '来源',
            dataIndex: 'source',
            key: 'source',
            width: 120,
        },
        {
            title: '消息内容',
            dataIndex: 'message',
            key: 'message',
            render: (text, record) => (
                <div>
                    <div>{text}</div>
                    {record.details && (
                        <Tooltip title={JSON.stringify(record.details, null, 2)}>
                            <span style={{ fontSize: '12px', color: '#1890ff', cursor: 'pointer' }}>查看详情</span>
                        </Tooltip>
                    )}
                </div>
            )
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                !record.is_resolved ? (
                    <Popconfirm
                        title="标记为已解决?"
                        description="确认该异常已处理完毕。"
                        onConfirm={() => onResolve(record.id)}
                        okText="是"
                        cancelText="否"
                    >
                        <Button size="small" type="primary">解决</Button>
                    </Popconfirm>
                ) : (
                    <Tag color="green">已解决</Tag>
                )
            ),
            width: 100,
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={alerts}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
        />
    );
};

export default AlertTable;
